import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useMappedState } from "redux-react-hook";
import {
  Button,
  DataList,
  DataListItem,
  DataListItemRow,
  DataListCell,
  DataListAction,
  DataListToggle,
  DataListContent,
  DataListItemCells,
  Dropdown,
  DropdownItem,
  DropdownPosition,
  DropdownSeparator,
  KebabToggle,
  Switch,
  Title,
  TextContent,
  TextList,
  TextListVariants,
  TextListItem,
  TextListItemVariants,
  Checkbox,
} from "@patternfly/react-core";
import { PencilAltIcon } from "@patternfly/react-icons";
import { Spinner } from "@patternfly/react-core";
import ProductInfoForm from "./ProductInfoForm";
import DeleteConfirmationModal from "../general/DeleteConfirmationModal";
import PublishConfirmationModal from "../general/PublishConfirmationModal";
import { REQUESTED_UPDATE_PRODUCT } from "../../redux/actions/productAction";

export default (props) => {
  const mapState = useCallback(
    (state) => ({
      productsMap: state.productState.productsMap,
      isUpdatingProduct: state.productState.isUpdatingProduct,
      isUpdatedProduct: state.productState.isUpdatedProduct,
    }),
    []
  );

  const { productsMap, isUpdatingProduct, isUpdatedProduct } = useMappedState(
    mapState
  );
  const dispatch = useDispatch();

  const [product, setProduct] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  useEffect(() => {
    if (props.productId && productsMap) {
      setProduct(
        Object.values(productsMap).find((value) => value.id === props.productId)
      );
    }
  }, [props.productId, productsMap]);

  useEffect(() => {
    if (!isUpdatingProduct && isUpdatedProduct) {
      setProduct(
        Object.values(productsMap).find((value) => value.id === props.productId)
      );
    }
  }, [isUpdatingProduct, isUpdatedProduct, productsMap, props.productId]);

  function onToggleAction(isActionOpen) {
    setIsActionOpen(isActionOpen);
  }

  function onSelectAction(event) {
    setIsActionOpen(!isActionOpen);
  }

  function handleModalToggle() {
    setIsModalOpen(!isModalOpen);
  }

  function handlePublishModalToggle() {
    setIsPublishModalOpen(!isPublishModalOpen);
  }

  function toggle(id) {
    const index = expanded.indexOf(id);
    const newExpanded =
      index >= 0
        ? [
          ...expanded.slice(0, index),
          ...expanded.slice(index + 1, expanded.length),
        ]
        : [...expanded, id];
    setExpanded(newExpanded);
  }

  const onClickEditProduct = useCallback(() => {
    // open the product info panel
    !expanded.includes("product-info-toggle") &&
      setExpanded([...expanded, "product-info-toggle"]);
    setIsEditingProduct(!isEditingProduct);
  }, [expanded, isEditingProduct]);

  function onEditSubmit(productInfo) {
    dispatch({
      type: REQUESTED_UPDATE_PRODUCT,
      payload: { product: productInfo },
    });
    setIsEditingProduct(false);
  }

  function onEditCancel() {
    setIsEditingProduct(false);
  }

  function renderProductInfoContent() {
    if (isEditingProduct) {
      return (
        <ProductInfoForm
          product={product}
          onSubmit={onEditSubmit}
          onCancel={onEditCancel}
        />
      );
    }

    return (
      <TextContent>
        <TextList component={TextListVariants.dl}>
          <TextListItem component={TextListItemVariants.dt}>
            Acronyms, Short Names, Key Components
          </TextListItem>
          <TextListItem component={TextListItemVariants.dd}>
            {product.acronyms}
          </TextListItem>
          <TextListItem component={TextListItemVariants.dt}>
            Link of detailed life cycle information
          </TextListItem>
          <TextListItem component={TextListItemVariants.dd}>
            {product.link}
          </TextListItem>
          <TextListItem component={TextListItemVariants.dt}>
            Support policies
          </TextListItem>
          <TextListItem component={TextListItemVariants.dd}>
            {product.related_links}
          </TextListItem>
          <TextListItem component={TextListItemVariants.dt}>
            Is component product
          </TextListItem>
          <TextListItem component={TextListItemVariants.dd}>
            <Checkbox
              label="Yes"
              isChecked={!!product.is_layered_product}
              aria-label="Is Component Product"
              id="is_layered_product_check"
              isDisabled
            />
          </TextListItem>
          {!!product.is_layered_product && (
            <>
              <TextListItem component={TextListItemVariants.dt}>
                Dependent Header Column
              </TextListItem>
              {product.product_extra_headers.map((item, index) => {
                return (
                  <TextListItem key={`textListItem-${index}`} component={TextListItemVariants.dd}>
                    {item.header}
                  </TextListItem>
                );
              })}
            </>
          )}
          <TextListItem component={TextListItemVariants.dt}>
            Show Last Minor Release
          </TextListItem>
          <TextListItem component={TextListItemVariants.dd}>
            <Switch
              id="show-last-minor-release"
              aria-label={
                !!product.show_last_minor_release ? `Enable` : `Disable`
              }
              label={!!product.show_last_minor_release ? `Enable` : `Disable`}
              labelOff={
                !!product.show_last_minor_release ? `Enable` : `Disable`
              }
              isChecked={!!product.show_last_minor_release}
            />
          </TextListItem>
        </TextList>
      </TextContent>
    );
  }

  const renderPencilIcon = useCallback(() => {
    if (isUpdatingProduct) {
      return <Spinner size="lg" />;
    } else {
      return (
        <Button
          variant="plain"
          aria-label="edit product"
          onClick={() => {
            onClickEditProduct();
          }}
        >
          <PencilAltIcon />
        </Button>
      );
    }
  }, [isUpdatingProduct, onClickEditProduct]);

  const productBriefInfo = useCallback(() => {
    let dataListItems = [];
    if (product) {
      dataListItems.push(
        <DataListCell key="product name">
          <Title
            id="product-name"
            headingLevel="h3"
            size="xl"
          >{`${product.name}`}</Title>
        </DataListCell>
      );
      product.is_draft &&
        dataListItems.push(
          <DataListCell key="is draft">
            <Button isDisabled variant="tertiary">
              Draft
            </Button>
          </DataListCell>
        );
      dataListItems.push(
        <DataListCell isIcon key="edit icon">
          {renderPencilIcon(isUpdatingProduct)}
        </DataListCell>
      );
    }
    return dataListItems;
  }, [product, isUpdatingProduct, renderPencilIcon]);

  useEffect(() => {
    productBriefInfo();
  }, [isUpdatingProduct, productBriefInfo]);

  const renderBody = useCallback(() => {
    if ((isUpdatingProduct && !isUpdatedProduct) || !product) {
      return <Spinner size="xl" />;
    } else {
      return (
        <>
          <DataList aria-label={`${product.acronyms} Info`}>
            <DataListItem
              aria-labelledby="Product Brief"
              isExpanded={expanded.includes("product-info-toggle")}
            >
              <DataListItemRow>
                <DataListToggle
                  onClick={() => toggle("product-info-toggle")}
                  isExpanded={expanded.includes("product-info-toggle")}
                  id="product-info-toggle"
                  aria-controls="product-info-expand"
                />
                <DataListItemCells dataListCells={productBriefInfo()} />
                <DataListAction
                  aria-labelledby="check-action-item check-action-action"
                  id="check-action-action"
                  aria-label="Actions"
                >
                  <Dropdown
                    isPlain
                    position={DropdownPosition.right}
                    isOpen={isActionOpen}
                    onSelect={onSelectAction}
                    toggle={<KebabToggle onToggle={onToggleAction} />}
                    dropdownItems={[
                      <DropdownItem
                        key="togglePublish"
                        component="button"
                        onClick={handlePublishModalToggle}
                      >
                        {product.is_draft
                          ? "Publish this product"
                          : "Draft this product"}
                      </DropdownItem>,
                      <DropdownSeparator key="separator" />,
                      <DropdownItem
                        key="remove"
                        component="button"
                        onClick={handleModalToggle}
                      >
                        Remove this product
                      </DropdownItem>,
                    ]}
                  />
                </DataListAction>
              </DataListItemRow>
              <DataListContent
                aria-label="Product Details"
                id="product-info-expand"
                isHidden={!expanded.includes("product-info-toggle")}
              >
                {renderProductInfoContent()}
              </DataListContent>
            </DataListItem>
          </DataList>
          <DeleteConfirmationModal
            name={product.name}
            type={"product"}
            productId={product.id}
            isModalOpen={isModalOpen}
            handleModalToggle={handleModalToggle}
          />
          <PublishConfirmationModal
            product={product}
            type={"product"}
            isModalOpen={isPublishModalOpen}
            handleModalToggle={handlePublishModalToggle}
          />
        </>
      );
    }
  }, [
    expanded,
    handleModalToggle,
    isUpdatedProduct,
    isUpdatingProduct,
    isActionOpen,
    isModalOpen,
    onSelectAction,
    productBriefInfo,
    product,
    toggle,
    isPublishModalOpen,
    handlePublishModalToggle,
  ]);

  useEffect(() => {
    renderBody();
  }, [isUpdatedProduct, product, renderBody]);

  return renderBody();
};
